// Pure transcript-upload draft/parse/serialize/validate helpers
// (transcripts.md module plan, UI Layering Sweep Phase 3). Extracted from the
// 934-line TranscriptUploadForm so the form stays a thin container/View pair
// and these rules are unit-testable without React. No React, no network.

import type { components } from "../api/schema";
import { isApiRequestError } from "./errors";

type TranscriptUtterance = components["schemas"]["TranscriptUtterance"];
type UtteranceRole = components["schemas"]["UtteranceRole"];

export type TranscriptUploadMode = "text" | "audio";
export type TextUtteranceInputMode = "table" | "json";

export interface TranscriptUtteranceDraft {
  endMs: string;
  id: string;
  role: UtteranceRole;
  startMs: string;
  text: string;
}

export interface TranscriptUploadSubmission {
  audioFile: File | null;
  includeMarkerIds: string[];
  mode: TranscriptUploadMode;
  occurredAt: string | null;
  suppressMarkerIds: string[];
  utterances: TranscriptUtterance[] | null;
  versionTag: string;
}

export type TranscriptUploadStatus =
  | { stage: "idle" }
  | { stage: "creating" }
  | { stage: "requesting_upload_url" }
  | { stage: "uploading"; uploadPercent: number | null }
  | { stage: "completing" }
  | { message: string; stage: "success" }
  | { message: string; stage: "validation_error" }
  | { message: string; stage: "api_error" }
  | { message: string; stage: "upload_error" };

export type TranscriptUploadProgress = (status: TranscriptUploadStatus) => void;

export type TranscriptUploadSubmit = (
  submission: TranscriptUploadSubmission,
  progress: TranscriptUploadProgress,
) => Promise<void>;

export const utteranceRoles: UtteranceRole[] = ["user", "assistant", "tool"];
let nextDraftId = 0;

export function createDraftRow(): TranscriptUtteranceDraft {
  nextDraftId += 1;
  return {
    endMs: "",
    id: `utterance-${String(nextDraftId)}`,
    role: "user",
    startMs: "",
    text: "",
  };
}

function createDraftRowFromUtterance(
  utterance: Omit<TranscriptUtterance, "speaker">,
): TranscriptUtteranceDraft {
  nextDraftId += 1;
  return {
    endMs: utterance.end_ms == null ? "" : String(utterance.end_ms),
    id: `utterance-${String(nextDraftId)}`,
    role: utterance.role,
    startMs: utterance.start_ms == null ? "" : String(utterance.start_ms),
    text: utterance.text,
  };
}

export function isUploadBusy(status: TranscriptUploadStatus): boolean {
  return (
    status.stage === "creating" ||
    status.stage === "requesting_upload_url" ||
    status.stage === "uploading" ||
    status.stage === "completing"
  );
}

export function uploadStatusMessage(
  status: TranscriptUploadStatus,
): string | null {
  switch (status.stage) {
    case "idle":
      return null;
    case "creating":
      return "Creating transcript.";
    case "requesting_upload_url":
      return "Preparing WAV upload.";
    case "uploading":
      return status.uploadPercent == null
        ? "Uploading WAV."
        : `Uploading WAV (${String(status.uploadPercent)}%).`;
    case "completing":
      return "Completing upload.";
    case "success":
    case "validation_error":
    case "api_error":
    case "upload_error":
      return status.message;
  }
}

export function parseMarkerIds(value: string): string[] {
  return value
    .split(/[\s,]+/u)
    .map((markerId) => markerId.trim())
    .filter((markerId) => markerId.length > 0);
}

function parseOptionalMs(
  value: string,
  rowNumber: number,
  label: string,
): number | null | string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    return [
      `Utterance ${String(rowNumber)} ${label} must be a`,
      "non-negative integer.",
    ].join(" ");
  }
  return parsed;
}

export function buildUtterances(
  rows: TranscriptUtteranceDraft[],
): TranscriptUtterance[] | string {
  if (rows.length === 0) {
    return "Add at least one utterance.";
  }

  const utterances: TranscriptUtterance[] = [];
  for (const [index, row] of rows.entries()) {
    const rowNumber = index + 1;
    const text = row.text.trim();
    if (text.length === 0) {
      return `Utterance ${String(rowNumber)} text is required.`;
    }

    const startMs = parseOptionalMs(row.startMs, rowNumber, "start ms");
    if (typeof startMs === "string") {
      return startMs;
    }
    const endMs = parseOptionalMs(row.endMs, rowNumber, "end ms");
    if (typeof endMs === "string") {
      return endMs;
    }
    if (startMs != null && endMs != null && endMs < startMs) {
      return `Utterance ${String(rowNumber)} end ms must be after start ms.`;
    }

    utterances.push({
      end_ms: endMs,
      role: row.role,
      speaker: null,
      start_ms: startMs,
      text,
    });
  }

  return utterances;
}

function draftMsToJsonValue(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
}

export function serializeDraftRows(rows: TranscriptUtteranceDraft[]): string {
  return JSON.stringify(
    rows.map((row) => ({
      end_ms: draftMsToJsonValue(row.endMs),
      role: row.role,
      start_ms: draftMsToJsonValue(row.startMs),
      text: row.text,
    })),
    null,
    2,
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value != null && !Array.isArray(value);
}

function hasOwnField(value: Record<string, unknown>, field: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, field);
}

function isUtteranceRole(value: unknown): value is UtteranceRole {
  return (
    typeof value === "string" && utteranceRoles.some((role) => role === value)
  );
}

function parseJsonMs(
  value: Record<string, unknown>,
  field: "start_ms" | "end_ms",
  rowNumber: number,
): number | null | string {
  if (!hasOwnField(value, field)) {
    return `Utterance ${String(rowNumber)} ${field} is required.`;
  }

  const rawValue = value[field];
  if (rawValue === null) {
    return null;
  }
  if (
    typeof rawValue !== "number" ||
    !Number.isSafeInteger(rawValue) ||
    rawValue < 0
  ) {
    return [
      `Utterance ${String(rowNumber)} ${field} must be a`,
      "non-negative integer or null.",
    ].join(" ");
  }
  return rawValue;
}

function parseJsonUtterance(
  value: unknown,
  rowNumber: number,
): Omit<TranscriptUtterance, "speaker"> | string {
  if (!isRecord(value)) {
    return `Utterance ${String(rowNumber)} must be an object.`;
  }

  if (!hasOwnField(value, "role")) {
    return `Utterance ${String(rowNumber)} role is required.`;
  }
  if (!isUtteranceRole(value.role)) {
    return [
      `Utterance ${String(rowNumber)} role must be one of:`,
      `${utteranceRoles.join(", ")}.`,
    ].join(" ");
  }

  if (!hasOwnField(value, "text")) {
    return `Utterance ${String(rowNumber)} text is required.`;
  }
  if (typeof value.text !== "string") {
    return `Utterance ${String(rowNumber)} text must be a string.`;
  }
  if (value.text.trim().length === 0) {
    return `Utterance ${String(rowNumber)} text is required.`;
  }

  const startMs = parseJsonMs(value, "start_ms", rowNumber);
  if (typeof startMs === "string") {
    return startMs;
  }
  const endMs = parseJsonMs(value, "end_ms", rowNumber);
  if (typeof endMs === "string") {
    return endMs;
  }
  if (startMs != null && endMs != null && endMs < startMs) {
    return `Utterance ${String(rowNumber)} end_ms must be after start_ms.`;
  }

  return {
    end_ms: endMs,
    role: value.role,
    start_ms: startMs,
    text: value.text,
  };
}

export function parseUtteranceJson(
  value: string,
):
  | { error: string; rows: null }
  | { error: null; rows: TranscriptUtteranceDraft[] } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return { error: "Utterances JSON must be valid JSON.", rows: null };
  }

  if (!Array.isArray(parsed)) {
    return { error: "Utterances JSON must be an array.", rows: null };
  }
  if (parsed.length === 0) {
    return { error: "Add at least one utterance.", rows: null };
  }

  const rows: TranscriptUtteranceDraft[] = [];
  for (const [index, item] of parsed.entries()) {
    const utterance = parseJsonUtterance(item, index + 1);
    if (typeof utterance === "string") {
      return { error: utterance, rows: null };
    }
    rows.push(createDraftRowFromUtterance(utterance));
  }

  return { error: null, rows };
}

export function isWavFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".wav") ||
    file.type === "audio/wav" ||
    file.type === "audio/x-wav"
  );
}

export function uploadErrorStatus(error: unknown): TranscriptUploadStatus {
  if (isApiRequestError(error)) {
    return {
      message: `${error.status.code}: ${error.status.message}`,
      stage: "api_error",
    };
  }
  if (error instanceof Error) {
    return { message: error.message, stage: "upload_error" };
  }
  return { message: "Transcript upload failed.", stage: "upload_error" };
}

export function validateSubmission(
  mode: TranscriptUploadMode,
  versionTag: string,
  occurredAt: string,
  rows: TranscriptUtteranceDraft[],
  selectedFile: File | null,
  includeMarkerIds: string,
  suppressMarkerIds: string,
): TranscriptUploadSubmission | string {
  const trimmedVersionTag = versionTag.trim();
  if (trimmedVersionTag.length === 0) {
    return "Version tag is required.";
  }
  const occurredAtValue = occurredAt.trim();
  const occurredAtDate =
    occurredAtValue.length > 0 ? new Date(occurredAtValue) : null;
  if (occurredAtDate && Number.isNaN(occurredAtDate.getTime())) {
    return "Occurred at must be a valid date and time.";
  }
  const occurredAtIso = occurredAtDate?.toISOString() ?? null;

  if (mode === "text") {
    const utterances = buildUtterances(rows);
    if (typeof utterances === "string") {
      return utterances;
    }
    return {
      audioFile: null,
      includeMarkerIds: parseMarkerIds(includeMarkerIds),
      mode,
      occurredAt: occurredAtIso,
      suppressMarkerIds: parseMarkerIds(suppressMarkerIds),
      utterances,
      versionTag: trimmedVersionTag,
    };
  }

  if (selectedFile == null) {
    return "Select a WAV file.";
  }
  if (!isWavFile(selectedFile)) {
    return "Select a WAV file.";
  }

  return {
    audioFile: selectedFile,
    includeMarkerIds: parseMarkerIds(includeMarkerIds),
    mode,
    occurredAt: occurredAtIso,
    suppressMarkerIds: parseMarkerIds(suppressMarkerIds),
    utterances: null,
    versionTag: trimmedVersionTag,
  };
}
